import { formatTaskCardDue, TaskCardProps } from 'shared/components/TaskCard';
import {
  ClosedOffer,
  getClosingOrClosedOffer,
  getPreClosingOffer,
  SentOffer,
  UnderContractOffer,
  useOfferContext,
  VAInProgressOffer,
} from 'sellDirect/OfferContext';
import {
  getHubLateCheckoutWizardRoute,
  getHubORVAScheduleRoute,
  getHubSellingRoute,
  getHubSSVAUploadRoute,
  getHubVirtualAssessmentQuestionsRoute,
} from 'shared/helpers/routes';
import { useHistory, generatePath } from 'react-router-dom';
import { History } from 'history';
import { ClosingTasks, HelloSignClosingTask } from 'sellDirect/helpers/closingTasks';
import { isTaskComplete, isHelloSignTaskComplete } from 'sellDirect/helpers/tasks';
import {
  getClosingChangeCloseDateRoute,
  getClosingCheckoutPage,
  getClosingContractRoute,
  getClosingDisclosuresRoute,
  getClosingGeneralAddenda,
  getClosingServiceProvidersRoute,
} from 'sellDirect/routes/SellDirectClosingRouter';
import { useSSVASubtasks } from 'sellDirect/helpers/useSSVASubtasks';
import { isServiceProvidersFormComplete } from 'sellDirect/helpers/closingDash/serviceProviders';
import { isDisclosuresFormComplete } from 'sellDirect/helpers/closingDash/disclosures';
import { ADDENDUM_ID_QUERY_PARAM } from 'sellDirect/components/closingDash/generalAddendums/GeneralAddendumWizard';
import { ZonedDateTime } from '@js-joda/core';
import {
  hasAnsweredOccupancyQuestion,
  OCCUPANCIES_FOR_ORVA,
} from 'sellDirect/helpers/virtualAssessmentQuestions';
import { isOpendoorAppEvoWebView } from 'shared/helpers/globals';
import { OdProtosWebData_ORVAState as ORVAState } from '__generated__/globalTypes';
import { shouldSeeNewDisclosureFlowFeature } from 'sellDirect/helpers/experiments';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

interface UseSellerTasksResult {
  loading: boolean;
  tasks?: TaskCardProps[];
}

export const UnderContractTaskTitles = {
  SERVICE_PROVIDER_INFO: 'Submit service provider info',
  DISCLOSURES: 'Complete disclosures',
  FINAL_WALKTHROUGH: 'Finalize closing details',
};

/** Gets SellDirect product tasks. */
export const useSellDirectTasks = (): UseSellerTasksResult => {
  const { hasUnknownReferralAttributionType, isPendingAgentReferralAcceptance } =
    useQualificationContext();
  const history = useHistory();
  const {
    loading: loadingOffer,
    shouldSeeAgentExperience,
    offer,
    experimentationData: { shouldSeeSsvaNooExperience, isEnhancedDisclosureFlowEnabled },
    refreshOffer,
  } = useOfferContext();
  const { addressUUID } = useQualificationContext();

  const preClosingOffer = getPreClosingOffer(offer);
  const { progress } = useSSVASubtasks();

  if (loadingOffer) {
    return { loading: true };
  }

  if (!offer || !offer.leadToken) {
    return { loading: false };
  }

  const shouldSeeEnhancedDisclosureFlow = shouldSeeNewDisclosureFlowFeature(
    offer.sentAt,
    isEnhancedDisclosureFlowEnabled,
  );

  const closingOrClosedOffer = getClosingOrClosedOffer(offer);

  if (closingOrClosedOffer && closingOrClosedOffer.closingTasks) {
    switch (closingOrClosedOffer.state) {
      case 'Finalized':
        /**
         * When the state of the offer is Finalized but the purchase agreement has been signed
         * we are waiting for the offer's state to change to under_contract.
         * While we wait (we poll inside SellDirectClosingContext) we return loading
         */
        if (closingOrClosedOffer.isPurchaseAgreementSigned) {
          return { loading: true };
        } else if (closingOrClosedOffer.isExpired) {
          return {
            loading: false,
            tasks: expiredTasks(
              addressUUID,

              shouldSeeAgentExperience,
              history,
              refreshOffer,
            ),
          };
        } else if (closingOrClosedOffer.isAccepted) {
          return {
            loading: false,
            tasks: [
              {
                title: shouldSeeAgentExperience
                  ? 'Next - Sign your client’s offer'
                  : 'Next - Sign your offer',
                analyticsName: 'sign-contract',
                url: getClosingContractRoute(),
                due: formatTaskCardDue(
                  closingOrClosedOffer.purchaseAgreementExpiresAt.toLocalDate(),
                ),
              },
            ],
          };
        }

        return {
          loading: false,
          tasks: [],
        };
      case 'UnderContract':
        return {
          loading: false,
          tasks: underContractTasks(
            closingOrClosedOffer,
            closingOrClosedOffer.closingTasks,
            shouldSeeAgentExperience,
            shouldSeeEnhancedDisclosureFlow,
          ),
        };
      case 'Closed':
        return {
          loading: false,
          tasks: closedTasks(
            closingOrClosedOffer,
            closingOrClosedOffer.closingTasks,
            shouldSeeAgentExperience,
          ),
        };
      case 'Withdrawn':
        return {
          loading: false,
          tasks: [],
        };
    }
  }

  if (offer.isExpired) {
    return {
      loading: false,
      tasks: expiredTasks(
        addressUUID,

        shouldSeeAgentExperience,
        history,
        refreshOffer,
      ),
    };
  }

  if (!preClosingOffer) {
    return {
      loading: false,
    };
  }

  const {
    orvaData: { orvaState },
    ssvaData: { ssvaState },
    expiresAt,
  } = preClosingOffer;

  // if VA has completed, return no tasks.
  if (offer.state === 'Finalizing') {
    return {
      loading: false,
      tasks: [],
    };
  }

  // if SSVA is in progress and is agent experience or is in the SSVA targeting experiment
  // return SSVA tasks.
  if (ssvaState === 'in_progress' && (shouldSeeAgentExperience || shouldSeeSsvaNooExperience)) {
    return {
      loading: false,
      tasks: getSsvaTasks(expiresAt ?? undefined, progress, shouldSeeAgentExperience),
    };
  }

  // if SSVA is not in progress and ORVA is scheduled, return no tasks.
  if (orvaState === ORVAState.ORVA_SCHEDULED) {
    return {
      loading: false,
      tasks: [],
    };
  }

  // Tasks specific for the SSVA Targeting Experiment
  if (shouldSeeSsvaNooExperience && (offer.state === 'Sent' || offer.state === 'VAInProgress')) {
    return {
      loading: false,
      tasks: getSsvaTargetingExperimentTasks(
        offer,
        shouldSeeAgentExperience,
        expiresAt ?? undefined,
        progress,
      ),
    };
  }

  let tasks: TaskCardProps[];
  if (shouldSeeAgentExperience) {
    // If the agent hasn't made a refer-or-represent decision, prompt them to do
    // so before SSVA
    if (hasUnknownReferralAttributionType) {
      tasks = [
        {
          title: 'Choose whether to refer or represent the homeowner',
          analyticsName: 'agent-refer-or-represent-from-selling-module-card',
          url: getHubSellingRoute(),
        },
      ];
    } else {
      tasks = getSsvaTasks(expiresAt ?? undefined, progress, shouldSeeAgentExperience);
    }
  } else {
    if (isPendingAgentReferralAcceptance) {
      tasks = [
        {
          title: 'Choose whether to accept the agent’s referral',
          analyticsName: 'agent-referral-acceptance-from-selling-module-card',
          url: getHubSellingRoute(),
        },
      ];
    } else {
      tasks = getOrvaTasks(expiresAt ?? undefined);
    }
  }

  // if SSVA is not in progress and ORVA hasn't been scheduled, returns ORVA tasks.
  return {
    loading: false,
    tasks,
  };
};

/** Get the tasks for customers in the SSVA Targeting experiment */
const getSsvaTargetingExperimentTasks = (
  offer: SentOffer | VAInProgressOffer,
  shouldSeeAgentExperience: boolean,
  preliminaryOfferExpiresAt?: ZonedDateTime,
  progress?: number | undefined,
): TaskCardProps[] => {
  // Agents should always get ssva tasks.
  if (shouldSeeAgentExperience) {
    return getSsvaTasks(preliminaryOfferExpiresAt, progress, shouldSeeAgentExperience);
  }

  // If the user has not answered the occupancy question, show the question page
  if (!hasAnsweredOccupancyQuestion(offer.occupancyAtVa)) {
    return [
      {
        title: 'Show us your home',
        analyticsName: 'va-question-task-from-selling-module-card',
        url: getHubVirtualAssessmentQuestionsRoute(),
        due: preliminaryOfferExpiresAt
          ? formatTaskCardDue(preliminaryOfferExpiresAt.toLocalDate())
          : undefined,
      },
    ];
  }

  // If the user answered Other or Owner occupied and was filtered to ORVA,
  // but did not schedule the ORVA right away, show the ORVA task
  if (OCCUPANCIES_FOR_ORVA.includes(offer.occupancyAtVa)) {
    return getOrvaTasks(preliminaryOfferExpiresAt);
  }

  // Else, the user answered SSVA but did not start uploading right away
  // so we show the SSVA tasks
  return getSsvaTasks(preliminaryOfferExpiresAt, progress, shouldSeeAgentExperience);
};

/** Get tasks for SSVA */
const getSsvaTasks = (
  preliminaryOfferExpiresAt?: ZonedDateTime,
  progress?: number,
  shouldSeeAgentExperience?: boolean,
): TaskCardProps[] => {
  return [
    {
      title: shouldSeeAgentExperience
        ? 'Show us your client’s home'
        : 'Finish your self-guided walkthrough',
      analyticsName: shouldSeeAgentExperience
        ? 'agent-ssva-from-selling-module-card'
        : 'ssva-from-selling-module-card',
      url: getHubSSVAUploadRoute(),
      progress,
      due: preliminaryOfferExpiresAt
        ? formatTaskCardDue(preliminaryOfferExpiresAt.toLocalDate())
        : undefined,
    },
  ];
};

const getOrvaTasks = (preliminaryOfferExpiresAt?: ZonedDateTime): TaskCardProps[] => {
  return [
    {
      title: 'Show us your home to finalize your offer',
      analyticsName: 'orva-from-selling-module-card',
      url: getHubORVAScheduleRoute(),
      due: preliminaryOfferExpiresAt
        ? formatTaskCardDue(preliminaryOfferExpiresAt.toLocalDate())
        : undefined,
    },
  ];
};

const underContractTasks = (
  offer: UnderContractOffer,
  closingTasks: ClosingTasks,
  shouldSeeAgentExperience: boolean,
  shouldSeeEnhancedDisclosureFlow: boolean,
): TaskCardProps[] => {
  const tasks: TaskCardProps[] = [
    {
      title: UnderContractTaskTitles.SERVICE_PROVIDER_INFO,
      analyticsName: 'service-providers',
      url: getClosingServiceProvidersRoute(),
      isDone: isServiceProvidersFormComplete(closingTasks),
    },
    {
      title: UnderContractTaskTitles.DISCLOSURES,
      analyticsName: 'disclosures',
      url: getClosingDisclosuresRoute(),
      isDone: isDisclosuresFormComplete(shouldSeeEnhancedDisclosureFlow, closingTasks, offer),
      disabled:
        shouldSeeEnhancedDisclosureFlow &&
        isDisclosuresFormComplete(shouldSeeEnhancedDisclosureFlow, closingTasks, offer),
    },
    {
      title: UnderContractTaskTitles.FINAL_WALKTHROUGH,
      analyticsName: 'final-walkthrough',
      url: getClosingCheckoutPage(),
      isDone:
        offer?.state === 'UnderContract' &&
        !!offer?.closingExperience?.digitalCheckout?.submittedAt,
    },
    // TODO: Probably need to add optional tasks like signing addendums
  ];

  tasks.push(...helloSignTasks(closingTasks, shouldSeeAgentExperience));
  return tasks;
};

const helloSignTasks = (
  closingTasks: ClosingTasks,
  shouldSeeAgentExperience: boolean,
): TaskCardProps[] => {
  const documentAction = shouldSeeAgentExperience ? 'Upload' : 'Sign';
  const tasks: TaskCardProps[] = [];
  /**
   * When there's no late checkout requested nor completed, the late checkout task is set to false.
   * When it's true, that means that we're either currently signing a late checkout addendum,
   * or it has already been signed
   */
  if (isTaskComplete(closingTasks.lateCheckout)) {
    const isLateCheckoutAddendumComplete = isHelloSignTaskComplete(
      closingTasks.signLateCheckoutAddendum,
    );
    tasks.push({
      title: `${documentAction} late checkout addendum`,
      analyticsName: `${documentAction.toLowerCase()}-late-checkout-addendum`,
      url: getHubLateCheckoutWizardRoute(),
      isDone: isLateCheckoutAddendumComplete,
      disabled: isLateCheckoutAddendumComplete,
    });
  }

  /**
   * We show the change COE date task only if the user is currently requesting a change of COE
   * or if the user has already completed it.
   *
   * signChangeCloseOfEscrowAddendum will be undefined if that's not the case
   */
  if (closingTasks.signChangeCloseOfEscrowAddendum) {
    const isChangeCOEComplete = isHelloSignTaskComplete(
      closingTasks.signChangeCloseOfEscrowAddendum,
    );
    tasks.push({
      title: `${documentAction} change close date addendum`,
      analyticsName: `${documentAction.toLowerCase()}-change-close-date-addendum`,
      url: getClosingChangeCloseDateRoute(),
      isDone: isChangeCOEComplete,
      disabled: isChangeCOEComplete,
    });
  }

  /** General addendum tasks */
  closingTasks.signFormAddendums?.forEach((task, idx) => {
    const formattedAddendumTask = formatAddendumTask(task, idx, documentAction);
    tasks.push(formattedAddendumTask);
  });

  return tasks;
};

const closedTasks = (
  offer: ClosedOffer,
  closingTasks: ClosingTasks,
  shouldSeeAgentExperience: boolean,
): TaskCardProps[] => {
  const tasks: TaskCardProps[] = [];
  tasks.push({
    title: UnderContractTaskTitles.FINAL_WALKTHROUGH,
    analyticsName: 'final-walkthrough',
    url: getClosingCheckoutPage(),
    isDone: !!offer?.closingExperience?.digitalCheckout?.submittedAt,
  });

  tasks.push(...helloSignTasks(closingTasks, shouldSeeAgentExperience));

  return tasks;
};

const expiredTasks = (
  addressUUID: string | undefined,
  shouldSeeAgentExperience: boolean,
  history: History,
  refreshOffer: () => void,
): TaskCardProps[] => {
  return [
    {
      title: shouldSeeAgentExperience ? 'Refresh your client’s offer' : 'Refresh your offer',
      analyticsName: 'refresh-offer-selling-module',
      onClick: () => {
        // For native mobile we will send them to the sell webview/tab to manually refresh
        if (!isOpendoorAppEvoWebView) {
          refreshOffer();
        }
        history.push(generatePath(getHubSellingRoute(), { addressUUID }));
      },
    },
  ];
};

const formatAddendumTask = (
  task: HelloSignClosingTask,
  idx: number,
  documentAction: string,
): TaskCardProps => ({
  title: `${documentAction} addendum ${idx + 1}`,
  analyticsName: `${documentAction.toLowerCase()}-addendum-${idx + 1}`,
  url: `${getClosingGeneralAddenda()}?${ADDENDUM_ID_QUERY_PARAM}=${task.id}`,
  // TODO: Make sure form addendum task updates to completed after agent upload document.
  isDone: isHelloSignTaskComplete(task),
  disabled: isHelloSignTaskComplete(task),
});
